import React, { type JSX } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconChevronLeft } from "@tabler/icons-react";
import { Button } from "../../../../components/common/button";
import { PageContainer } from "../../../../components/common/page-container";
import { Heading, Text } from "../../../../components/common/typography";
import { AppRoutes } from "../../../../common/routes/app_routes";
import {
  LegalNumberedList,
  type NumberedItem,
  type NumberingConfig,
} from "../components/legal_numbered_list";

export type ContentItem = {
  type: "heading" | "paragraph" | "list" | "numbered_list";
  text?: string | JSX.Element;
  bullets?: (string | JSX.Element)[];
  isBold?: boolean;
  startNumber?: number | NumberingConfig;
  sectionTitle?: string;
  sectionSubtitle?: string | JSX.Element;
  items?: NumberedItem[];
};

type LegalContentPageProps = {
  title: string;
  content: ContentItem[];
};

const LegalContentPage: React.FC<LegalContentPageProps> = ({ title, content }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(AppRoutes.INDEX);
    }
  };

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          size="sm"
          className="group mb-6"
          leftIcon={
            <IconChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
          }
          onClick={handleBack}
        >
          Back
        </Button>

        <div className="bg-bgWhite rounded-lg shadow-lg p-8">
          <Heading variant="h1" className="mb-8 border-b-2 border-borderDefault pb-4">
            {title}
          </Heading>

          <div>
            {content.map((item, index) => {
              if (item.type === "heading") {
                return (
                  <Heading key={index} variant="h2" className="mt-8 mb-4">
                    {item.text}
                  </Heading>
                );
              }
              if (item.type === "paragraph") {
                return (
                  <Text
                    key={index}
                    variant={item.isBold ? "bodyBold" : "body"}
                    tone={item.isBold ? "dark" : "body"}
                    className="mb-4 leading-relaxed"
                  >
                    {item.text}
                  </Text>
                );
              }
              if (item.type === "list" && item.bullets) {
                return (
                  <ul key={index} className="list-disc pl-6 mb-6 space-y-2">
                    {item.bullets.map((bullet, i) => (
                      <li key={i}>
                        <Text
                          variant={item.isBold ? "bodyBold" : "body"}
                          tone={item.isBold ? "dark" : "body"}
                          as="span"
                          className="leading-relaxed"
                        >
                          {bullet}
                        </Text>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (item.type === "numbered_list" && item.items && item.startNumber) {
                return (
                  <div key={index} className="mb-6">
                    <LegalNumberedList
                      startNumber={item.startNumber}
                      items={item.items}
                      isBold={item.isBold}
                      sectionTitle={item.sectionTitle}
                      sectionSubtitle={item.sectionSubtitle}
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default LegalContentPage;
